import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { SelectOptionType, TransferAccountStep } from './TransferAccount.meta';
import { FormValues, useTransferAccount } from './useTransferAccount';
import { TransferAccountConfirmType } from './TransferAccountConfirmType/TransferAccountConfirmType';
import { TransferAccountConfirmNumber } from './TransferAccountConfirmNumber/TransferAccountConfirmNumber';
import { TransferAccountPopover } from './TransferAccountPopover/TransferAccountPopover';
import { ErrorModal } from '../ErrorModal/ErrorModal';
import { LoadingModal } from '../LoadingModal/LoadingModal';
import { TransferAccountDTO } from '../../api/useTransferAccountsOptionsQuery';
import { useTransferMoneyContext } from '../../hooks/useTransferMoneyContext';
import { usePlaidLink } from '../../../../_hooks/plaid/usePlaidLink';
import { ModalHeader } from '../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalBody } from '../../../../CommonComponents/OPL/Modal/ModalBody';
import { Modal } from '../../../../CommonComponents/OPL/Modal/Modal';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { ModalFooter } from '../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Icon } from '../../../../CommonComponents/OPL/Icon/Icon';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { FullPageLoading } from '../../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { isConsumerArea as selectIsConsumerArea } from '../../../../CommonSelectors/CommonAreaSelector';

interface Props {
  onClose(): void;
  onBack(): void;
}

export const TransferAccount: React.FC<Props> = ({ onClose, onBack }) => {
  const [step, setStep] = useState(TransferAccountStep.TransferAccount);

  const [selectedAccount, setSelectedAccount] = useState<TransferAccountDTO | null>(null);

  const { clientId } = useTransferMoneyContext();

  const { isLoading, isError, refetch, data } = useTransferAccount();

  const isConsumerArea = useSelector(selectIsConsumerArea);

  const { formBag, selectOptions } = data;
  const {
    formState: { errors },
  } = formBag;

  const {
    open: openPlaidModal,
    isLoading: isLoadingPlaidUpdateLink,
    queryError: openPlaidError,
    mutationError: upsertLinkError,
  } = usePlaidLink({
    clientId,
    onStart: () => {
      setStep(TransferAccountStep.PlaidModalVisible);
    },
    onSuccess: () => {
      refetch();
    },
    onExit: () => {
      setStep(TransferAccountStep.TransferAccount);
    },
    onHandOff: () => {
      setStep(TransferAccountStep.TransferAccount);
    },
  });

  const onSubmit = (values: FormValues) => {
    if (values.selectedAccount) {
      setSelectedAccount(values.selectedAccount.value);
      setStep(TransferAccountStep.TransferAccountConfirmType);
    }
  };

  if (isLoading) {
    return <LoadingModal />;
  }

  if (isError) {
    return <ErrorModal onClose={onClose} onRetry={refetch} />;
  }

  if (isLoadingPlaidUpdateLink) {
    return <FullPageLoading />;
  }

  if (step === TransferAccountStep.PlaidModalVisible) {
    return null;
  }

  if (step === TransferAccountStep.TransferAccountConfirmType && selectedAccount) {
    return (
      <TransferAccountConfirmType
        selectedAccount={selectedAccount}
        onClose={onClose}
        setStep={setStep}
      />
    );
  }

  if (step === TransferAccountStep.TransferAccountConfirmNumber && selectedAccount) {
    return <TransferAccountConfirmNumber selectedAccount={selectedAccount} onClose={onClose} />;
  }

  return (
    <Modal isOpen onRequestClose={onClose}>
      <ModalHeader>Transfer Account</ModalHeader>
      <ModalCloseButton onClick={onClose} />

      {(openPlaidError || upsertLinkError) && (
        <Alert
          message={
            openPlaidError?.message ||
            upsertLinkError?.message ||
            'Sorry, something went wrong. Please try again.'
          }
          variant="error"
          my="16"
          mx="24"
        />
      )}

      <form onSubmit={formBag.handleSubmit(onSubmit)}>
        <ModalBody>
          Bring over your investments from external brokerage accounts. Only Full ACATS and In-Kind
          Transfer are supported.
          <TransferAccountPopover>
            <Icon className="far fa-info-circle" ml="4" />
          </TransferAccountPopover>
          <Box mt="16">
            <Controller
              control={formBag.control}
              name="selectedAccount"
              render={({ field: { value } }) => {
                return (
                  <Select<SelectOptionType>
                    label="Select Account"
                    value={value}
                    placeholder="Select an account..."
                    options={selectOptions}
                    onChange={opt => {
                      if (opt) {
                        formBag.setValue('selectedAccount', opt);
                        formBag.clearErrors('selectedAccount');
                      }
                    }}
                    formVariant={errors.selectedAccount ? 'error' : undefined}
                    helperText={get(errors, 'selectedAccount.message')}
                    isPortal
                  />
                );
              }}
            />
          </Box>
          {isConsumerArea && (
            <Button
              mt="32"
              variant="link"
              leftIcon="fa fa-plus"
              onClick={() => {
                openPlaidModal();
              }}
            >
              Link New Account
            </Button>
          )}
        </ModalBody>

        <ModalFooter>
          <Box flex={1}>
            <Button leftIcon="fas fa-chevron-left" variant="secondary" onClick={onBack}>
              Back
            </Button>
          </Box>

          <Button variant="subtleInactive" onClick={onClose} mr="8">
            Cancel
          </Button>

          <Button type="submit">Next</Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
